import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, User, ArrowLeft, MapPin, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userType, setUserType] = useState("");
  const [country, setCountry] = useState("");
  const [legalStatus, setLegalStatus] = useState("");

  useEffect(() => {
    // Get user type from session storage
    const storedType = sessionStorage.getItem("selectedUserType");
    if (storedType) {
      setUserType(storedType);
    } else {
      // Redirect to profile choice if no type selected
      navigate("/profil");
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!country) {
      setError("Veuillez sélectionner le pays du logement.");
      return;
    }
    if (!legalStatus) {
      setError("Veuillez sélectionner votre statut juridique.");
      return;
    }
    // Gate cote front (UX): meme regle que cote backend. Le backend reste la
    // source de verite et renverra une 400 si on contourne ce check.
    if (country === "FR" && legalStatus === "particulier") {
      setError(
        "HostProtect n'est pas disponible pour les particuliers situés en France. " +
        "Notre offre concerne les propriétaires bailleurs immatriculés (SAS, SARL, EI, " +
        "auto-entrepreneur) ou les particuliers situés en Espagne."
      );
      return;
    }
    if (country === "OTHER") {
      setError("HostProtect n'est pas encore disponible dans votre pays. Nous couvrons actuellement la France et l'Espagne.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    if (password.length < 12) {
      setError("Le mot de passe doit contenir au moins 12 caractères.");
      return;
    }
    // Mirror backend rule: at least 3 of {lowercase, uppercase, digit, symbol}
    const classes =
      (/[a-z]/.test(password) ? 1 : 0) +
      (/[A-Z]/.test(password) ? 1 : 0) +
      (/\d/.test(password) ? 1 : 0) +
      (/[^A-Za-z0-9]/.test(password) ? 1 : 0);
    if (classes < 3) {
      setError("Le mot de passe doit combiner au moins 3 types de caractères parmi : minuscules, majuscules, chiffres, symboles.");
      return;
    }

    setLoading(true);

    const result = await register(email, password, name, userType, country, legalStatus);
    
    if (result.success) {
      toast.success("Compte créé avec succès !");
      sessionStorage.removeItem("selectedUserType");
      navigate("/checkout");
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const userTypeLabel = userType === "professionnel" ? "Professionnel" : "Particulier";

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans'] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-lg w-full max-w-md" data-testid="register-card">
          <Link 
            to="/profil" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#19294C] mb-6 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Changer de profil
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#19294C] font-['Outfit']">
              Créer un compte
            </h1>
            <p className="mt-2 text-slate-600">
              Inscription en tant que <span className="font-medium text-[#00B8E4]">{userTypeLabel}</span>
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-slate-700">Nom complet</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jean Dupont"
                  className="pl-10 border-slate-300 focus:ring-[#00B8E4] focus:border-[#00B8E4]"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="pl-10 border-slate-300 focus:ring-[#00B8E4] focus:border-[#00B8E4]"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="country" className="text-slate-700">Pays du logement</Label>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                <select
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                  data-testid="register-country-select"
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md bg-white text-sm focus:ring-2 focus:ring-[#00B8E4] focus:border-[#00B8E4] appearance-none cursor-pointer"
                >
                  <option value="">Sélectionnez le pays</option>
                  <option value="ES">Espagne</option>
                  <option value="FR">France</option>
                  <option value="OTHER">Autre pays</option>
                </select>
              </div>
            </div>

            <div>
              <Label htmlFor="legal_status" className="text-slate-700">Statut juridique</Label>
              <div className="relative mt-1">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                <select
                  id="legal_status"
                  value={legalStatus}
                  onChange={(e) => setLegalStatus(e.target.value)}
                  required
                  data-testid="register-legal-status-select"
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md bg-white text-sm focus:ring-2 focus:ring-[#00B8E4] focus:border-[#00B8E4] appearance-none cursor-pointer"
                >
                  <option value="">Sélectionnez votre statut</option>
                  <option value="sas">SAS / SASU</option>
                  <option value="sarl">SARL / EURL</option>
                  <option value="ei">Entreprise Individuelle (EI)</option>
                  <option value="auto_entrepreneur">Auto-entrepreneur / Micro-entreprise</option>
                  <option value="particulier">Particulier (personne physique)</option>
                </select>
              </div>
              {country === "FR" && legalStatus === "particulier" && (
                <p className="mt-2 text-xs text-red-600" data-testid="legal-status-warning">
                  HostProtect n'est pas disponible pour les particuliers en France. Sélectionnez un statut immatriculé ou choisissez l'Espagne.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">Mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 border-slate-300 focus:ring-[#00B8E4] focus:border-[#00B8E4]"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">Minimum 12 caractères, avec au moins 3 types parmi : minuscules, majuscules, chiffres, symboles.</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-slate-700">Confirmer le mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 border-slate-300 focus:ring-[#00B8E4] focus:border-[#00B8E4]"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00B8E4] hover:bg-[#00a5ce] text-white py-3 font-semibold mt-2"
              data-testid="register-submit-btn"
            >
              {loading ? "Création..." : "Créer mon compte"}
            </Button>
          </form>

          <p className="text-center text-sm text-slate-600 mt-6">
            Vous avez déjà un compte ?{" "}
            <Link to="/connexion" className="text-[#00B8E4] hover:underline font-medium">
              Se connecter
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
